import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface TicketingUser {
  id: string;
  name: string;
  email: string;
  role: string; // 'user' | 'admin' | 'tracker' | 'manager' | 'super_admin'
  userType: string; // 'franchisee' | 'admin'
}

interface TicketingSession {
  token: string;
  user: TicketingUser;
}

const STORAGE_KEY = 'ticketingAuth';
const STAFF_ROLES = ['admin', 'manager', 'super_admin'];

// Single source of truth for the ticketing session — replaces the six
// scattered, mutually-inconsistent localStorage keys (adminId, isAdmin,
// currentUser, userName, userDetails, adminUserDetails) and the four
// different "is this user an admin" checks that used to be spread across
// the app.
@Injectable({ providedIn: 'root' })
export class TicketingAuthService {
  private sessionSubject = new BehaviorSubject<TicketingSession | null>(this.readSession());
  session$ = this.sessionSubject.asObservable();

  private readSession(): TicketingSession | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private normalizeUser(raw: any): TicketingUser {
    const role = raw.role;
    return {
      id: raw.id || raw._id,
      name: raw.name || raw.username,
      email: raw.email,
      role,
      userType: raw.userType || (STAFF_ROLES.includes(role) ? 'admin' : 'franchisee'),
    };
  }

  // Accepts the {token, user} shape returned by /ticketing/login and
  // /ticketing/sso/validate alike.
  setSessionFromLoginResponse(response: { token: string; user: any }): void {
    const session: TicketingSession = { token: response.token, user: this.normalizeUser(response.user) };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    this.sessionSubject.next(session);
  }

  // Keeps the cached session (and anywhere reading currentUser, e.g. the
  // sidebar) in sync immediately after a successful self-service email
  // change, without forcing a re-login.
  updateSessionEmail(email: string): void {
    const session = this.sessionSubject.value;
    if (!session) return;
    const updated: TicketingSession = { ...session, user: { ...session.user, email } };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    this.sessionSubject.next(updated);
  }

  logout(): void {
    // Clear this session plus every legacy key old code scattered across
    // localStorage — nothing used to clear these before, which was itself a bug.
    [STORAGE_KEY, 'adminId', 'isAdmin', 'currentUser', 'userName', 'userDetails', 'adminUserDetails']
      .forEach((key) => localStorage.removeItem(key));
    this.sessionSubject.next(null);
  }

  get currentUser(): TicketingUser | null {
    return this.sessionSubject.value?.user || null;
  }

  get token(): string | null {
    return this.sessionSubject.value?.token || null;
  }

  isLoggedIn(): boolean {
    return !!this.sessionSubject.value;
  }

  isAdmin(): boolean {
    return STAFF_ROLES.includes(this.currentUser?.role || '');
  }

  isSuperAdmin(): boolean {
    return this.currentUser?.role === 'super_admin';
  }

  isFranchisee(): boolean {
    return this.isLoggedIn() && !this.isAdmin();
  }
}
